import {Component, inject, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {of} from 'rxjs';

import {ConversationsComponent} from '../conversations/conversations.component';
import {ConversationPreviewService} from '../conversation-preview.service';
import {MasterThreadService} from '../../../services/objects/master-thread.service';
import {ChatbotsService} from '../../../services/objects/chatbots.service';
import {AssistantsService} from '../../../services/objects/assistants.service';
import {ClientRetentionService} from '../../../services/objects/client-retention.service';
import {IConversation} from '../../../models/conversation.model';
import {IChatbotOptions} from '../../../models/chatbot-options.model';
import {ClientService} from '../../../services/auth/client.service';

@Component({
  selector: 'app-conversation-visual-fixture',
  standalone: true,
  imports: [ConversationsComponent],
  template: `
    <main class="conversation-visual-fixture">
      <aside class="fixture-notice" aria-label="Local test data">
        <strong>Local test preview</strong>
        <span>These are synthetic conversations, not the selected client's history.</span>
        <a href="/ai/conversations?tab=1">Open client conversations</a>
      </aside>
      <app-conversations></app-conversations>
    </main>
  `,
  styles: [`
    .conversation-visual-fixture {
      display: block;
    }
    .fixture-notice {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 12px;
      padding: 12px 20px;
      background: #fff6d9;
      color: #513d00;
    }
    .fixture-notice a {
      color: inherit;
      text-decoration: underline;
    }
  `],
  providers: [
    {provide: ClientService, useValue: {client: {id: 'fixture-client'}}},
    {
      provide: MasterThreadService,
      useValue: {
        getMasterThreadsAsync: () => of([createFixtureConversation()]),
        getAttachment: () => of(new Blob([Uint8Array.from(atob('iVBORw0KGgoAAAANSUhEUgAAAGAAAABICAIAAACGBWc0AAAArklEQVR4nO3QQQ2AAAwEwSpBCcLQxBtXmEBCf82VTHLvzWXqvc92x/W0+2un0g6ldQABAgQIUHAHECBAgAAFd2rj6ckOIECAAAEK7gACBAgQoOAOoA5o4+nJDiBAgAABCu4AAgQIEKDgDqAOaOPpyQ4gQIAAAQruAAIECBCg4A6gDmjj6ckOIECAAAEK7gACBAgQoOAOoA5o4+nJDiBAgAABCu4AAgQIEKDgDqBmH//U7uilPv4IAAAAAElFTkSuQmCC'), value => value.charCodeAt(0))], {type: 'image/png'})),
        approveMasterThreadAsync: (_: string, thread: IConversation) => of({...thread, approved: true}),
        declineMasterThreadAsync: (_: string, thread: IConversation) => of({...thread, dismissed: true}),
        deleteMasterThreadAsync: () => of(void 0)
      }
    },
    {
      provide: ChatbotsService,
      useValue: {
        getAllAsync: () => of([createFixtureChatbot()])
      }
    },
    {
      provide: AssistantsService,
      useValue: {
        getAllAsync: () => of([
          {id: 'fixture-assistant', assistantId: 'fixture-assistant', name: 'Sales assistant'}
        ])
      }
    },
    {
      provide: ClientRetentionService,
      useValue: {
        getAsync: () => of({
          clientId: 'fixture-client',
          conversationRetentionDays: 90,
          conversationCategories: ['Product recommendations', 'Support'],
          usesLegacyChatbotCategories: false
        })
      }
    },
    {
      provide: ConversationPreviewService,
      useValue: {
        open: () => undefined
      }
    },
    {
      provide: MatDialog,
      useValue: {
        open: () => ({afterClosed: () => of(false)})
      }
    },
    {
      provide: MatSnackBar,
      useValue: {
        open: () => undefined
      }
    }
  ]
})
export class ConversationVisualFixtureComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  ngOnInit(): void {
    const routeConversationId = this.route.snapshot.paramMap.get('id');
    const queryConversationId = this.route.snapshot.queryParamMap.get('conversation');

    if (routeConversationId && !queryConversationId) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {conversation: routeConversationId},
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }
  }
}

function createFixtureConversation(): IConversation {
  const intro = 'Here are two products that match the request.';
  const outro = 'The shell is lighter, while the parka is warmer.';
  const longReference = 'spring-weather-layering-product-reference-with-a-very-long-unbroken-token-'.repeat(4);

  return {
    id: 'fixture-thread',
    clientId: 'fixture-client',
    email: 'fixture@example.com',
    hubSpotId: 'fixture-hubspot',
    shopAssistantId: 'fixture-chatbot',
    thread: 'fixture-provider-thread',
    category: 'Product recommendations',
    messages: [
      {
        user: true,
        messageId: 'fixture-message',
        text: `I need a light jacket for mixed spring weather and this long pasted reference should wrap: ${longReference}`,
        attachmentSummaries: [
          {
            fileName: 'weather-reference-photo.png',
            attachmentId: 'a'.repeat(64),
            expiresAtUtc: '2099-01-01T00:00:00Z',
            contentType: 'image/png',
            size: 231
          },
          {
            fileName: 'size-guide.pdf',
            contentType: 'application/pdf',
            size: 842233
          }
        ],
        pageTitle: `Fixture product category ${longReference}`,
        pageUrl: `https://example.com/products/jackets/${longReference}?utm_source=visual-fixture&utm_campaign=${longReference}`,
        timestampUtc: '2026-06-24T08:00:00Z',
      },
      {
        user: false,
        text: `${intro} ${outro}`,
        assistantId: 'fixture-assistant',
        assistantSelectionReason: `Fixture route for product-card transcript review with a long routing label ${longReference}`,
        timestampUtc: '2026-06-24T08:00:05Z',
        uiUpdates: [
          {
            function: 'display_product_cards',
            displayMode: 'block',
            textOffset: intro.length + 1,
            timestampUtc: '2026-06-24T08:00:06Z',
            dataJson: JSON.stringify({
              presentation: 'vertical-carousel',
              cards: [
                {
                  id: 'fixture-shell',
                  name: `Luma shell jacket ${longReference}`,
                  description: `Water resistant shell with taped seams and a long merchandising note ${longReference}`,
                  url: 'https://example.com/products/luma-shell',
                  salePriceText: '1299 NOK',
                  regularPriceText: '1599 NOK',
                },
                {
                  id: 'fixture-parka',
                  name: `Nord parka ${longReference}`,
                  description: `Insulated jacket for cold spring mornings with a long merchandising note ${longReference}`,
                  url: 'https://example.com/products/nord-parka',
                  priceText: '1899 NOK',
                },
              ],
            }),
          },
        ],
      },
      {
        user: false,
        text: `A compact recommendation can also appear before supporting answer text ${longReference}`,
        timestampUtc: '2026-06-24T08:00:10Z',
        uiUpdates: [
          {
            function: 'display_product_cards',
            displayMode: 'block',
            renderPosition: 'assistant-end',
            presentation: 'compact-carousel',
            timestampUtc: '2026-06-24T08:00:11Z',
            dataJson: JSON.stringify({
              cards: [
                {
                  id: 'fixture-base-layer',
                  name: `Merino base layer ${longReference}`,
                  description: `Soft base layer for changing conditions ${longReference}`,
                  url: 'https://example.com/products/base-layer',
                  priceText: '699 NOK',
                },
              ],
            }),
          },
        ],
      },
    ],
    created: new Date('2026-06-24T08:00:00Z'),
    lastModified: new Date('2026-06-24T08:00:12Z'),
    thumbUp: true,
    thumbDown: false,
    approved: false,
    dismissed: false,
    cookieValues: [],
    errorMessages: [],
    assistantIds: ['fixture-assistant'],
    tags: ['visual-fixture', 'product-cards', longReference],
  };
}

function createFixtureChatbot(): IChatbotOptions {
  return {
    id: 'fixture-chatbot',
    clientId: 'fixture-client',
    name: 'Fixture Chatbot',
    frontEndOptions: {
      productCardPresentation: 'horizontal',
      styling: {
        cardBtnBgColor: '#1f2937',
        cardBtnBgColorHover: '#111827',
        cardBtnColor: '#ffffff',
        cardBtnColorHover: '#ffffff',
        cardMaxWidth: '273px',
        cardImageAspectRatio: '3:4',
        cardImageBackgroundColor: '#f2f2f2',
        cardBorderRadius: '12px',
        cardPriceCurrentColor: '#b42318',
      },
    },
  } as IChatbotOptions;
}
